import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, KeyRound, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_MODES,
  NOTIFICATION_SCOPES,
  useNotificationStore,
} from "../stores/notification-store";
import { useFinanceStore } from "../stores/finance-store";
import { buildNotificationSchedules } from "../lib/notification-schedules";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Separator } from "./ui/separator";
import { PasswordInput } from "./PasswordInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const CHANNEL_PLACEHOLDERS = {
  email: "voce@email.com",
  telegram: "@seu_usuario",
  whatsapp: "+55 31 99999-0000",
};

function toggleValue(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export function NotificationSettingsCard() {
  const {
    consent,
    channels,
    fetchConsent,
    saveConsent,
    grantFullMode,
    revokeGrant,
    addChannel,
    verifyChannel,
    removeChannel,
    syncSchedules,
  } = useNotificationStore();
  const { transactions } = useFinanceStore();

  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const [ceremonyMode, setCeremonyMode] = useState(NOTIFICATION_MODES.SIMPLE);
  const [ceremonyScopes, setCeremonyScopes] = useState([]);
  const [ceremonyChannels, setCeremonyChannels] = useState([]);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [channelDrafts, setChannelDrafts] = useState({});
  const [verificationCodes, setVerificationCodes] = useState({});

  useEffect(() => {
    fetchConsent();
  }, [fetchConsent]);

  const openCeremony = () => {
    setCeremonyMode(consent.mode || NOTIFICATION_MODES.SIMPLE);
    setCeremonyScopes(consent.scopes || []);
    setCeremonyChannels(consent.channels || []);
    setPassword("");
    setCeremonyOpen(true);
  };

  const publishSchedules = async (scopes) => {
    const items = buildNotificationSchedules(transactions, { scopes });
    await syncSchedules(items);
  };

  const handleDisable = async () => {
    const result = await saveConsent({ enabled: false });
    if (result.success) {
      toast.success("Notificações desativadas e autorização revogada");
    } else {
      toast.error(result.error || "Erro ao desativar notificações");
    }
  };

  const handleToggle = (checked) => {
    if (checked) {
      openCeremony();
      return;
    }
    handleDisable();
  };

  const handleConfirmCeremony = async () => {
    if (!ceremonyScopes.length) {
      toast.error("Selecione ao menos um conteúdo para notificar");
      return;
    }
    if (!ceremonyChannels.length) {
      toast.error("Selecione ao menos um canal");
      return;
    }
    if (ceremonyMode === NOTIFICATION_MODES.FULL && !password) {
      toast.error("Digite sua senha para autorizar o modo completo");
      return;
    }

    setSaving(true);
    try {
      if (ceremonyMode === NOTIFICATION_MODES.FULL) {
        const granted = await grantFullMode(password);
        if (!granted.success) {
          toast.error(granted.error || "Erro ao autorizar");
          return;
        }
      }

      const result = await saveConsent({
        enabled: true,
        mode: ceremonyMode,
        scopes: ceremonyScopes,
        channels: ceremonyChannels,
      });

      if (!result.success) {
        toast.error(result.error || "Erro ao salvar autorização");
        return;
      }

      await publishSchedules(ceremonyScopes);
      toast.success("Notificações autorizadas");
      setCeremonyOpen(false);
      setPassword("");
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeGrant = async () => {
    const result = await revokeGrant();
    if (result.success) {
      await saveConsent({ mode: NOTIFICATION_MODES.SIMPLE });
      toast.success("Autorização revogada. Suas notificações voltaram ao modo simples.");
    } else {
      toast.error(result.error || "Erro ao revogar autorização");
    }
  };

  const handleAddChannel = async (channelType) => {
    const destination = (channelDrafts[channelType] || "").trim();
    if (!destination) {
      toast.error("Informe o destino do canal");
      return;
    }

    const result = await addChannel(channelType, destination);
    if (result.success) {
      toast.success("Código de verificação enviado");
      setChannelDrafts((current) => ({ ...current, [channelType]: "" }));
    } else {
      toast.error(result.error || "Erro ao cadastrar canal");
    }
  };

  const handleVerifyChannel = async (channel) => {
    const code = (verificationCodes[channel.id] || "").trim();
    if (!code) {
      toast.error("Informe o código recebido");
      return;
    }

    const result = await verifyChannel(channel.id, code);
    if (result.success) {
      toast.success("Canal verificado");
      setVerificationCodes((current) => ({ ...current, [channel.id]: "" }));
    } else {
      toast.error(result.error || "Código inválido");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {consent.enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          Notificações
        </CardTitle>
        <CardDescription>
          Avisos de vencimento de contas, faturas e recebimentos
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="notifications-enabled">Permitir o envio de notificações?</Label>
            <p className="text-sm text-muted-foreground">
              Você escolhe o que pode ser enviado e por onde. Revogável a qualquer momento.
            </p>
          </div>
          <Switch
            id="notifications-enabled"
            checked={Boolean(consent.enabled)}
            onCheckedChange={handleToggle}
            data-testid="notifications-toggle"
          />
        </div>

        {consent.enabled && (
          <>
            <Separator />

            <div className="space-y-1 text-sm">
              <p className="font-medium">
                Modo: {consent.mode === NOTIFICATION_MODES.FULL ? "Conteúdo completo" : "Aviso simples"}
              </p>
              <p className="text-muted-foreground">
                Conteúdos: {(consent.scopes || [])
                  .map((scope) => NOTIFICATION_SCOPES.find((item) => item.value === scope)?.label)
                  .filter(Boolean)
                  .join(", ") || "nenhum"}
              </p>
              <Button variant="link" className="h-auto p-0 text-sm" onClick={openCeremony}>
                Alterar autorização
              </Button>
            </div>

            {consent.grant_stale && (
              <div className="alert-banner alert-warning">
                <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Autorização desatualizada</p>
                  <p className="text-sm mt-1">
                    Sua senha mudou e a autorização anterior não abre mais o cofre.
                    Autorize novamente para voltar a receber notificações detalhadas.
                  </p>
                </div>
              </div>
            )}

            {consent.grant_active && (
              <div className="rounded-lg border p-3 text-sm space-y-2">
                <p className="flex items-center gap-2 font-medium">
                  <KeyRound className="w-4 h-4" />
                  Autorização de conteúdo ativa
                </p>
                <p className="text-muted-foreground">
                  Último uso: {consent.grant_last_used_at
                    ? new Date(consent.grant_last_used_at).toLocaleString("pt-BR")
                    : "ainda não utilizada"}
                </p>
                <Button variant="outline" size="sm" onClick={handleRevokeGrant} data-testid="revoke-grant-btn">
                  Revogar autorização
                </Button>
              </div>
            )}

            <Separator />

            <div className="space-y-3">
              <p className="text-sm font-medium">Canais</p>
              {NOTIFICATION_CHANNELS.filter((channel) => (consent.channels || []).includes(channel.value))
                .map((channel) => {
                  const registered = channels.find((item) => item.channel_type === channel.value);

                  return (
                    <div key={channel.value} className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{channel.label}</span>
                        {registered?.verified && (
                          <span className="flex items-center gap-1 text-xs text-success">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Verificado
                          </span>
                        )}
                      </div>

                      {!registered && (
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            value={channelDrafts[channel.value] || ""}
                            placeholder={CHANNEL_PLACEHOLDERS[channel.value]}
                            onChange={(event) => setChannelDrafts((current) => ({
                              ...current,
                              [channel.value]: event.target.value,
                            }))}
                          />
                          <Button onClick={() => handleAddChannel(channel.value)}>Enviar código</Button>
                        </div>
                      )}

                      {registered && !registered.verified && (
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            value={verificationCodes[registered.id] || ""}
                            placeholder="Código de 6 dígitos"
                            inputMode="numeric"
                            onChange={(event) => setVerificationCodes((current) => ({
                              ...current,
                              [registered.id]: event.target.value,
                            }))}
                          />
                          <Button onClick={() => handleVerifyChannel(registered)}>Verificar</Button>
                        </div>
                      )}

                      {registered && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => removeChannel(registered.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Remover canal
                        </Button>
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={ceremonyOpen} onOpenChange={(open) => !saving && setCeremonyOpen(open)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Autorizar notificações</DialogTitle>
            <DialogDescription>
              Escolha quanto o servidor pode saber para montar suas mensagens.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label>O que podemos enviar?</Label>
              <button
                type="button"
                onClick={() => setCeremonyMode(NOTIFICATION_MODES.SIMPLE)}
                className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${
                  ceremonyMode === NOTIFICATION_MODES.SIMPLE ? "border-primary bg-primary/10" : "hover:bg-muted/50"
                }`}
              >
                <p className="font-medium">Aviso simples</p>
                <p className="text-muted-foreground">
                  "Você tem 3 contas vencendo amanhã." O servidor só conhece datas e quantidades.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setCeremonyMode(NOTIFICATION_MODES.FULL)}
                disabled={!consent.full_mode_available}
                className={`w-full rounded-lg border p-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  ceremonyMode === NOTIFICATION_MODES.FULL ? "border-primary bg-primary/10" : "hover:bg-muted/50"
                }`}
              >
                <p className="font-medium">Conteúdo completo</p>
                <p className="text-muted-foreground">
                  "Conta 'Energia' (R$ 250,00) vence amanhã." Exige autorizar o servidor a abrir
                  seus dados apenas para montar as notificações.
                </p>
                {!consent.full_mode_available && (
                  <p className="mt-1 text-xs text-warning">Indisponível neste ambiente.</p>
                )}
              </button>
            </div>

            <div className="space-y-2">
              <Label>Sobre o que?</Label>
              {NOTIFICATION_SCOPES.map((scope) => (
                <label key={scope.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={ceremonyScopes.includes(scope.value)}
                    onChange={() => setCeremonyScopes((current) => toggleValue(current, scope.value))}
                  />
                  {scope.label}
                </label>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Por onde?</Label>
              {NOTIFICATION_CHANNELS.map((channel) => (
                <label key={channel.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={ceremonyChannels.includes(channel.value)}
                    onChange={() => setCeremonyChannels((current) => toggleValue(current, channel.value))}
                  />
                  {channel.label}
                </label>
              ))}
            </div>

            {ceremonyMode === NOTIFICATION_MODES.FULL && (
              <div className="space-y-2">
                <Label htmlFor="grant-password">Confirme sua senha</Label>
                <PasswordInput
                  id="grant-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Sua senha"
                />
                <p className="text-xs text-muted-foreground">
                  A senha confirma que é você. A chave do cofre é entregue uma única vez e fica
                  guardada de forma protegida, usada só para montar as notificações.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCeremonyOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmCeremony} disabled={saving} data-testid="confirm-consent-btn">
                {saving ? "Autorizando..." : "Autorizar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
